/**
 * Schedule WBS as a collapsible tree, with each activity's Cognara proposal
 * state rolled up onto its parent packages.
 *
 * Nesting comes from the dotted activity code (1 → 1.1 → 1.1.2 → 1.1.2.5),
 * which is the WBS code in this schedule. Summary rows supply the package
 * names; any missing level gets a synthetic node so the tree never breaks.
 */
import { useMemo, useState, useCallback, useEffect, useRef } from 'react';
import { Badge } from '@/components/ui/badge';
import { ChevronRight, ChevronDown, AlertTriangle, Pencil, Flag } from 'lucide-react';

const BAND_STYLE = {
  HIGH: 'bg-emerald-500/15 text-emerald-600 border-emerald-500/30',
  MEDIUM: 'bg-amber-500/15 text-amber-600 border-amber-500/30',
  LOW: 'bg-rose-500/15 text-rose-600 border-rose-500/30',
};
const STATUS_STYLE = {
  PENDING: 'bg-slate-500/15 text-slate-500 border-slate-500/30',
  ACCEPTED: 'bg-emerald-500/15 text-emerald-600 border-emerald-500/30',
  REJECTED: 'bg-rose-500/15 text-rose-600 border-rose-500/30',
  COMMITTED: 'bg-blue-500/15 text-blue-600 border-blue-500/30',
};

function buildTree(activities) {
  const nodes = new Map();

  const ensure = (code) => {
    if (nodes.has(code)) return nodes.get(code);
    const node = { code, name: '', children: [], activity: null, isSynthetic: true };
    nodes.set(code, node);
    const parts = code.split('.');
    if (parts.length > 1) {
      const parent = ensure(parts.slice(0, -1).join('.'));
      parent.children.push(node);
    }
    return node;
  };

  activities.forEach(a => {
    const code = a.activityCode || a.activityId;
    const node = ensure(code);
    node.activity = a;
    node.name = a.activityName;
    node.isSynthetic = false;
  });

  // Roll proposal state up so a collapsed package still tells the truth.
  const roll = (node) => {
    const agg = { PENDING: 0, ACCEPTED: 0, REJECTED: 0, COMMITTED: 0,
                  proposed: 0, objects: 0, leaves: 0, disagreements: 0 };
    const p = node.activity?.proposal;
    if (p) {
      agg[p.status] = (agg[p.status] || 0) + 1;
      agg.proposed += 1;
      agg.objects += p.proposedCount || 0;
      if (p.disagreement) agg.disagreements += 1;
    }
    if (node.activity && !node.activity.isSummary) agg.leaves += 1;
    node.children.sort((x, y) => {
      const nx = x.code.split('.').map(Number);
      const ny = y.code.split('.').map(Number);
      for (let i = 0; i < Math.max(nx.length, ny.length); i++) {
        const d = (nx[i] || 0) - (ny[i] || 0);
        if (d) return d;
      }
      return 0;
    });
    node.children.forEach(c => {
      const sub = roll(c);
      Object.keys(agg).forEach(k => { agg[k] += sub[k]; });
    });
    node.agg = agg;
    return agg;
  };

  const roots = [...nodes.values()].filter(n => !n.code.includes('.'));
  roots.forEach(roll);
  roots.sort((a, b) => Number(a.code) - Number(b.code));
  return roots;
}

function matches(node, q, statusFilter) {
  const a = node.activity;
  const p = a?.proposal;
  const textOk = !q
    || (node.code || '').toLowerCase().includes(q)
    || (node.name || '').toLowerCase().includes(q);
  const statusOk = statusFilter === 'ALL'
    || (statusFilter === 'UNPROPOSED' ? (!p && a && !a.isSummary) : p?.status === statusFilter);
  if (textOk && statusOk) return true;
  return node.children.some(c => matches(c, q, statusFilter));
}

export function WbsProposalTree({ activities, selectedId, onSelect, search, statusFilter,
                                 buildup, focusCodes }) {
  const roots = useMemo(() => buildTree(activities || []), [activities]);
  const [collapsed, setCollapsed] = useState(() => new Set());
  const focusRef = useRef(null);

  // While the dry run plays, open the packages that contain whatever is
  // starting now and bring the first one into view — the point of showing the
  // build-up against the WBS is that you can see WHERE in the schedule it sits.
  const focusKey = (focusCodes || []).join(',');
  useEffect(() => {
    if (!focusCodes?.length) return;
    setCollapsed(prev => {
      if (!prev.size) return prev;
      const next = new Set(prev);
      focusCodes.forEach(code => {
        const parts = code.split('.');
        for (let i = 1; i <= parts.length; i++) next.delete(parts.slice(0, i).join('.'));
      });
      return next;
    });
    const t = setTimeout(() => {
      focusRef.current?.scrollIntoView({ block: 'center', behavior: 'smooth' });
    }, 60);
    return () => clearTimeout(t);
  }, [focusKey]);  // eslint-disable-line react-hooks/exhaustive-deps

  const toggle = useCallback((code) => {
    setCollapsed(prev => {
      const next = new Set(prev);
      if (next.has(code)) next.delete(code); else next.add(code);
      return next;
    });
  }, []);

  const q = (search || '').trim().toLowerCase();
  const filtering = !!q || statusFilter !== 'ALL';

  const renderNode = (node, depth) => {
    if (filtering && !matches(node, q, statusFilter)) return null;
    const a = node.activity;
    const p = a?.proposal;
    const isLeaf = !node.children.length;
    const isOpen = filtering || !collapsed.has(node.code);
    const isSelected = p && selectedId === a.activityId;
    const agg = node.agg || {};
    const built = buildup ? buildup[node.code] : null;
    const isStarting = !!built?.isNew;

    return (
      <div key={node.code}>
        <div
          ref={isStarting && (focusCodes || [])[0] === node.code ? focusRef : null}
          role="button"
          tabIndex={0}
          onClick={() => { if (p) onSelect(a.activityId); else if (!isLeaf) toggle(node.code); }}
          onKeyDown={(e) => { if (e.key === 'Enter') { if (p) onSelect(a.activityId); else if (!isLeaf) toggle(node.code); } }}
          className={`flex items-center gap-1.5 pr-2 py-1 border-b cursor-pointer transition-colors
            ${isStarting ? 'bg-emerald-500/20 hover:bg-emerald-500/25'
              : built ? 'bg-blue-500/5 hover:bg-blue-500/10'
              : 'hover:bg-muted/50'}
            ${isSelected ? 'bg-muted' : ''}`}
          style={{ paddingLeft: `${8 + depth * 14}px` }}
          data-testid={p ? `cognara-activity-${a.activityId}` : `wbs-node-${node.code}`}
        >
          {!isLeaf ? (
            <button onClick={(e) => { e.stopPropagation(); toggle(node.code); }}
                    className="shrink-0 text-muted-foreground hover:text-foreground"
                    data-testid={`wbs-toggle-${node.code}`}>
              {isOpen ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />}
            </button>
          ) : <span className="w-3.5 shrink-0" />}

          <span className={`text-[10px] font-mono shrink-0 ${isLeaf ? 'text-muted-foreground' : 'text-foreground/80'}`}>
            {node.code}
          </span>
          <span className={`text-xs truncate ${isLeaf ? '' : 'font-medium'}`}>
            {node.name || '(package)'}
          </span>
          {a?.isMilestone && <Flag className="w-3 h-3 text-blue-500 shrink-0" />}
          {a?.isCriticalPath && (
            <Badge variant="outline" className="text-[9px] h-4 px-1 border-rose-500/40 text-rose-600 shrink-0">CP</Badge>
          )}

          <div className="ml-auto flex items-center gap-1 shrink-0">
            {built && (
              <Badge variant="outline"
                     className={`text-[9px] h-4 px-1 ${isStarting
                       ? 'border-emerald-500/50 text-emerald-600'
                       : 'border-blue-500/40 text-blue-600'}`}
                     data-testid={`wbs-built-${node.code}`}>
                {isStarting ? 'Starting now' : 'Built'} · {built.objectCount}
              </Badge>
            )}
            {p ? (
              <>
                {p.edited && <Pencil className="w-3 h-3 text-blue-500" />}
                {p.disagreement && (
                  <span title="Cognara disagrees with the link already on this activity">
                    <AlertTriangle className="w-3 h-3 text-amber-500" />
                  </span>
                )}
                <Badge variant="outline" className={`text-[9px] h-4 px-1 ${BAND_STYLE[p.confidenceBand] || ''}`}>
                  {Math.round((p.confidence || 0) * 100)}%
                </Badge>
                <Badge variant="outline" className={`text-[9px] h-4 px-1 ${STATUS_STYLE[p.status] || ''}`}>
                  {p.status.charAt(0) + p.status.slice(1).toLowerCase()}
                </Badge>
                <span className="text-[10px] text-muted-foreground w-14 text-right">{p.proposedCount} obj</span>
              </>
            ) : isLeaf ? (
              <>
                <Badge variant="outline" className="text-[9px] h-4 px-1 text-muted-foreground">No proposal</Badge>
                <span className="text-[10px] text-muted-foreground w-14 text-right">
                  {a?.linkedObjectCount ? `${a.linkedObjectCount} linked` : '—'}
                </span>
              </>
            ) : (
              <>
                {agg.disagreements ? (
                  <Badge variant="outline" className="text-[9px] h-4 px-1 border-amber-500/30 text-amber-600">
                    {agg.disagreements} conflict{agg.disagreements > 1 ? 's' : ''}
                  </Badge>
                ) : null}
                {agg.ACCEPTED ? (
                  <Badge variant="outline" className={`text-[9px] h-4 px-1 ${STATUS_STYLE.ACCEPTED}`}>{agg.ACCEPTED} acc</Badge>
                ) : null}
                {agg.PENDING ? (
                  <Badge variant="outline" className={`text-[9px] h-4 px-1 ${STATUS_STYLE.PENDING}`}>{agg.PENDING} pend</Badge>
                ) : null}
                <span className="text-[10px] text-muted-foreground w-14 text-right">
                  {agg.proposed}/{agg.leaves} act
                </span>
              </>
            )}
          </div>
        </div>
        {isOpen && node.children.map(c => renderNode(c, depth + 1))}
      </div>
    );
  };

  if (!activities?.length) return null;
  return <div data-testid="cognara-wbs-tree">{roots.map(r => renderNode(r, 0))}</div>;
}

export default WbsProposalTree;
